import BaseApi from '@/api/Base/BaseApi'
import { omitNilOrEmptyStringFromObject } from '@/utils/common'

import {
  AddClassGroupReferencePayload,
  CreateLabelingNodePayload,
  CreateObjectPayload,
  CreateTagObjectPayload,
  CreateTagToImagesPayload,
  GetClassificationItemsPayload,
  GetClassificationItemsResponse,
  GetLabelingNodeItemsPayload,
  GetLabelingNodeItemsResponse,
  GetMagicWandObjectPayload,
  GetMagicWandObjectResponse,
  GetUserMetaResponse,
  GetUserShortcutsResponse,
  LabelingNodeInfo,
  LabelingNodeItem,
  LabelingObject,
  SaveObjectsPayload,
  UndoRedoResponse,
  UpdateAssignedLabelersPayload,
  UpdateLabelingNodePayload,
  UpdateLabelingNodeSettingsPayload,
  UpdateObjectPayload,
  UpdateUserMetaPayload,
  UpdateUserShortcutsPayload,
  UpdateObjectOrder,
  DeleteObjectsPayload,
  ItemHistory,
  RunClusteringPayload,
  SelectClusteringItemsPayload,
  ClusteringInfo,
  GetLabelAssistantItemsPayload,
  UpdateLabelAssistantPayload,
  UpdateLabelAssistantResponse,
  RunSemiAutoLabelingPayload,
  GetSemiAutoLabelingItemsResponse,
  GetSemiAutoLabelingReportResponse,
  UpdateSemiAutoLabelingThresholdPayload,
  DeleteSemiAutoLabelingObjectsPayload,
  GetSemiAutoLabelingModelsResponse,
  GetTabularLabelingItemsResponse,
  UpdateRetrainSemiAutoSettingsPayload,
  GetRetrainSemiAutoSettingsResponse,
  GetSemiAutoRetrainItemsResponse,
  SemiAutoRetrainItem,
  ToggleColumSelectionPayload,
  SemiAutoLabelingItemsPayload
} from '@/api/LabelingNode/types'
import { LabelingClass } from '@/api/ClassGroup/types'
import { CommonListPayload } from '@/api/DatasetNode/types'
import { ModelVersionListResponse, ExternalModelListForSemiAutoLabelingResponse } from '@/api/ModelVersion/types'

const ROOT = '/labeling'
const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class LabelingNodeApi extends BaseApi {
  createLabelingNode(payload: CreateLabelingNodePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post<LabelingNodeInfo>(`/project/${projectId}/labeling/create`, rest)
  }

  getNodeDetails(nodeId: string) {
    return this.agent.get<LabelingNodeInfo>(`${ROOT}/${nodeId}`)
  }

  updateNodeDetails(payload: UpdateLabelingNodePayload) {
    const { labelingNodeId, name, ...rest } = payload

    return this.agent.put<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}`, { ...rest, name: name?.trim() })
  }

  updateNodeSettings(payload: UpdateLabelingNodeSettingsPayload) {
    const { labelingNodeId, ...rest } = payload
    return this.agent.put<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}/setting`, rest)
  }

  updateAssignRole(labelingNodeId: string, payload: { role: string }) {
    return this.agent.put<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}/assign-role`, payload)
  }

  updateAssignedLabelers(payload: UpdateAssignedLabelersPayload) {
    const { labelingNodeId, ...rest } = payload
    return this.agent.put(`${ROOT}/${labelingNodeId}/change-labeler`, rest)
  }

  addClassGroupReference(payload: AddClassGroupReferencePayload) {
    const { labelingNodeId, classGroupId } = payload
    return this.agent.put(`${ROOT}/${labelingNodeId}/classgroup`, { classGroupId })
  }

  removeClassGroupReference(labelingNodeId: string) {
    return this.agent.delete(`${ROOT}/${labelingNodeId}/classgroup`)
  }

  getLabelingNodeClassGroup(labelingNodeId: string) {
    return this.agent.get<any>(`${ROOT}/${labelingNodeId}/classgroup`)
  }

  getLabelingItems(nodeId: string, options?: GetLabelingNodeItemsPayload) {
    const {
      itemsPerPage,
      page,
      sortDir,
      sortKey,
      status,
      isSelectedForClustering,
    } = options ?? { ...defaultPaginationOptions }

    return this.agent.get<GetLabelingNodeItemsResponse>(`${ROOT}/${nodeId}/items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
        status,
        isSelectedForClustering,
      },
    })
  }

  getFileDetails(sourceId: string) {
    return this.agent.get<LabelingNodeItem>(`/labeling-item/${sourceId}`)
  }

  /** @deprecated */
  toggleFileFinished(sourceId: string) {
    return this.agent.post<LabelingNodeItem>(`/labeling-item/${sourceId}/toggle-finished`)
  }

  // --------- LABELING ---------
  getUserClasses(labelingNodeId: string) {
    return this.agent.get<LabelingClass[]>(`${ROOT}/${labelingNodeId}/user/classes`)
  }

  updateUserClassOrder(labelingNodeId: string, classIds: string[]) {
    return this.agent.put<LabelingClass[]>(`${ROOT}/${labelingNodeId}/user/classes/reorder`, { classIds })
  }

  getObjectsOnFile(sourceId: string) {
    return this.agent.get<any>(`/labeling-item/${sourceId}/objects`)
  }

  saveObjects(payload: SaveObjectsPayload) {
    return this.agent.put<LabelingObject[]>(`${ROOT}/objects`, payload)
  }

  createObject(payload: CreateObjectPayload) {
    return this.agent.post<LabelingObject>(`${ROOT}/object`, payload)
  }

  updateObject(payload: UpdateObjectPayload) {
    return this.agent.put<LabelingObject>(`${ROOT}/object`, payload)
  }

  deleteObject(objectId: string) {
    return this.agent.delete<LabelingObject>(`/labeling-object/${objectId}`)
  }

  deleteObjects(payload: DeleteObjectsPayload) {
    return this.agent.delete<LabelingObject[]>('/labeling-object/objects', { data: payload })
  }

  getMagicWandObject(payload: GetMagicWandObjectPayload) {
    const { labelingItemId, ...rest } = payload
    return this.agent.post<GetMagicWandObjectResponse>(`/labeling-item/${labelingItemId}/magic-wand`, rest)
  }

  getUserMeta(labelingNodeId: string) {
    return this.agent.get<GetUserMetaResponse>(`${ROOT}/${labelingNodeId}/user-meta`)
  }

  getLabelAssistantItems(labelingNodeId: string, options?: GetLabelAssistantItemsPayload) {
    const {
      itemsPerPage,
      page,
      isCreatedByUser,
      classes,
    } = options ?? { ...defaultPaginationOptions }
    return this.agent.get<GetLabelingNodeItemsResponse>(`${ROOT}/${labelingNodeId}/label-assistant-items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        isCreatedByUser,
        classes,
      },
    })
  }

  getSelectedLabelAssistantItems(labelingNodeId: string) {
    return this.agent.get<LabelingNodeItem[]>(`${ROOT}/${labelingNodeId}/selected-label-assistant-items`)
  }

  updateLabelAssistant(payload: UpdateLabelAssistantPayload) {
    const { labelingNodeId, labelingItemId, labelingPromptItemIds } = payload
    return this.agent.put<UpdateLabelAssistantResponse>(`${ROOT}/${labelingNodeId}/label-assistant`, { labelingItemId, labelingPromptItemIds })
  }

  updateUserMeta(payload: UpdateUserMetaPayload) {
    return this.agent.post(`${ROOT}/user-meta`, payload)
  }

  undoAction(labelingItemId: string) {
    return this.agent.put<UndoRedoResponse>(`/labeling-item/${labelingItemId}/undo`)
  }

  redoAction(labelingItemId: string) {
    return this.agent.put<UndoRedoResponse>(`/labeling-item/${labelingItemId}/redo`)
  }

  /** @deprecated */
  finishLabeling(labelingNodeId: string) {
    return this.agent.post(`${ROOT}/${labelingNodeId}/finish-labeling`)
  }

  /** @deprecated */
  finishAllLabelingItems(labelingNodeId: string) {
    return this.agent.post<LabelingNodeInfo>(`/labeling/${labelingNodeId}/finish-all-items`)
  }

  // 명시적 라벨링 종료
  explicitFinishLabeling(labelingNodeId: string) {
    return this.agent.post<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}/finish-labeling`)
  }

  restartLabeling(labelingNodeId: string) {
    return this.agent.post<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}/restart-labeling`)
  }

  finishReview(labelingNodeId: string) {
    return this.agent.post<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}/finish-review`)
  }

  restartReview(labelingNodeId: string) {
    return this.agent.post<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}/restart-review`)
  }

  // --------- CLASSIFICATION ---------
  getClassificationItems(payload: GetClassificationItemsPayload) {
    const { labelingNodeId, page, itemsPerPage, sortKey, sortDir, ...rest } = payload

    return this.agent.get<GetClassificationItemsResponse>(`/labeling/${labelingNodeId}/classification-items`, {
      params: {
        page,
        num_per_page: itemsPerPage ?? 100,
        sort_key: sortKey,
        sort_dir: sortDir,
        ...rest,
      },
    })
  }

  addTag(payload: CreateTagObjectPayload) {
    return this.agent.post<LabelingNodeItem>('/labeling/tag-object', payload)
  }

  addTagToImages(payload: CreateTagToImagesPayload) {
    return this.agent.post<any>('/labeling/tag-object-images', payload)
  }

  /** @deprecated */
  skipItem(sourceId: string) {
    return this.agent.post(`/labeling-item/${sourceId}/skip`)
  }

  // --------- shortcut ---------
  getUserShortcuts() {
    return this.agent.get<GetUserShortcutsResponse>(`${ROOT}/shortcut-key`)
  }

  updateUserShortcuts(payload: UpdateUserShortcutsPayload) {
    return this.agent.put(`${ROOT}/shortcut-key`, payload)
  }

  deleteLabelingNode(labelingNodeId: string) {
    return this.agent.delete(`${ROOT}/${labelingNodeId}`)
  }

  completeLabelingItems(labelingNodeId: string, statuses: any[]) {
    return this.agent.post<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}/complete-all-items`, {
      statuses,
    })
  }

  completeLabelingItem(labelingItemId: string) {
    return this.agent.post<LabelingNodeItem>(`/labeling-item/${labelingItemId}/complete`)
  }

  reorderLabelingObjects(labelingItemId: string, payload: { objectOrders: UpdateObjectOrder[] }) {
    return this.agent.put<LabelingNodeItem>(`/labeling-item/${labelingItemId}/reorder-objects`, payload)
  }

  deleteAllObjectsInItem(labelingItemId: string) {
    return this.agent.delete(`/labeling-item/${labelingItemId}/objects`)
  }

  getItemHistory(labelingItemId: string) {
    return this.agent.get<ItemHistory[]>(`/labeling-item/${labelingItemId}/history`)
  }

  // clustering
  getClustering(labelingNodeId: string) {
    return this.agent.get<ClusteringInfo>(`${ROOT}/${labelingNodeId}/clustering`)
  }

  runClustering(payload: RunClusteringPayload) {
    const { labelingNodeId, ...rest } = payload
    return this.agent.post(`${ROOT}/${labelingNodeId}/clustering`, rest)
  }

  selectClusteringItems(payload: SelectClusteringItemsPayload) {
    const { labelingNodeId, ...rest } = payload
    return this.agent.put(`${ROOT}/${labelingNodeId}/select-clustering`, rest)
  }

  getNotLabeledItems(labelingNodeId: string, status: string) {
    return this.agent.get<{ labelingNodeId: string, notLabeledItems: number }>(`${ROOT}/${labelingNodeId}/not-labeled-items`, {
      params: {
        status,
      },
    })
  }

  getIncompleteItems(labelingNodeId: string, status?: string) {
    return this.agent.get<{ labelingNodeId: string, incompleteItems: number }>(`${ROOT}/${labelingNodeId}/incomplete-items`, {
      params: {
        status,
      },
    })
  }

  // semi-auto labeling
  getSemiAutoLabelingModels(labelingNodeId: string) {
    return this.agent.get<GetSemiAutoLabelingModelsResponse>(`${ROOT}/${labelingNodeId}/semi-auto-labeling/models`)
  }

  runSemiAutoLabeling(payload: RunSemiAutoLabelingPayload) {
    const { labelingNodeId, ...rest } = payload
    return this.agent.post(`${ROOT}/${labelingNodeId}/semi-auto-labeling`, rest)
  }

  getSemiAutoLabelingReport(labelingNodeId: string, reportId: string) {
    return this.agent.get<GetSemiAutoLabelingReportResponse>(`${ROOT}/${labelingNodeId}/report/${reportId}`)
  }

  updateSemiAutoLabelingThreshold(payload: UpdateSemiAutoLabelingThresholdPayload) {
    const { labelingNodeId, reportId, ...rest } = payload
    return this.agent.put(`${ROOT}/${labelingNodeId}/report/${reportId}/confidence-threshold`, rest)
  }

  deleteSemiAutoLabelingObjects(payload: DeleteSemiAutoLabelingObjectsPayload) {
    const { labelingNodeId, reportId, ...rest } = payload
    return this.agent.delete(`${ROOT}/${labelingNodeId}/report/${reportId}/objects`, { data: rest })
  }

  getSemiAutoLabelingItems(labelingNodeId: string, reportId: string, options?: SemiAutoLabelingItemsPayload) {
    const {
      itemsPerPage,
      page,
      sortKey,
      sortDir,
      hasObjects,
      operation,
      score,
      rangeScore,
      boundaryRange,
    } = options ?? { ...defaultPaginationOptions }

    return this.agent.get<GetSemiAutoLabelingItemsResponse>(`${ROOT}/${labelingNodeId}/report/${reportId}/items`, {
      params: omitNilOrEmptyStringFromObject({
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
        hasObjects,
        operation,
        score,
        rangeScore,
        boundaryRange,
      }),
    })
  }

  getSemiAutoRetrainItems(labelingNodeId: string, reportId: string, options?: CommonListPayload) {
    const {
      itemsPerPage,
      page,
      sortKey,
      sortDir,
    } = options ?? { ...defaultPaginationOptions }

    return this.agent.get<GetSemiAutoRetrainItemsResponse>(`${ROOT}/${labelingNodeId}/report/${reportId}/retrain-items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getSemiAutoLabelingReportList(labelingNodeId: string) {
    return this.agent.get<GetSemiAutoLabelingReportResponse[]>(`${ROOT}/${labelingNodeId}/report/list`)
  }

  getSemiAutoLabelingVersionedModelList(labelingNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page } = options ?? defaultPaginationOptions
    return this.agent.get<ModelVersionListResponse>(`${ROOT}/${labelingNodeId}/semi-auto-labeling/versioned-models`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        model_size: 'LARGE',
      },
    })
  }

  getExternalModelListForSemiAutoLabeling(labelingNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page } = options ?? defaultPaginationOptions

    return this.agent.get<ExternalModelListForSemiAutoLabelingResponse>(`${ROOT}/${labelingNodeId}/semi-auto-labeling/external-models`, {
      params: {
        page,
        num_per_page: itemsPerPage,
      },
    })
  }

  getRetrainSemiAutoSettings(labelingNodeId: string) {
    return this.agent.get<GetRetrainSemiAutoSettingsResponse>(`${ROOT}/${labelingNodeId}/retrain-semi-auto-settings`)
  }

  updateRetrainSemiAutoSettings(payload: UpdateRetrainSemiAutoSettingsPayload) {
    const { labelingNodeId, ...rest } = payload
    return this.agent.post(`${ROOT}/${labelingNodeId}/retrain-semi-auto-settings`, rest)
  }

  toggleUseRetrain(labelingNodeId: string, reportItemId: string) {
    return this.agent.post<SemiAutoRetrainItem>(`${ROOT}/${labelingNodeId}/report/${reportItemId}/toggle-use-retrain`)
  }

  // ----------------- TABULAR, TIMESERIES -----------------
  // 사용자 지정 필터를 '저장'하는 api. '적용'이 아님.
  addSettingFilter(labelingNodeId: string, payload: any) {
    return this.agent.post<any>(`${ROOT}/${labelingNodeId}/setting-filters`, payload)
  }

  deleteSettingFilter(labelingNodeId: string, filterId: string) {
    return this.agent.delete(`${ROOT}/${labelingNodeId}/filter/${filterId}`)
  }

  updateSettingFilter(labelingNodeId: string, filterId: string, payload: any) {
    return this.agent.put<any>(`${ROOT}/${labelingNodeId}/filter/${filterId}`, payload)
  }

  getTabularLabelingItems(labelingNodeId: string, options?: any) {
    const {
      itemsPerPage,
      page,
      sortDir,
      sortKey,
      isLabeled,
    } = options ?? { ...defaultPaginationOptions }

    return this.agent.get<GetTabularLabelingItemsResponse>(`${ROOT}/${labelingNodeId}/tabular-items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
        isLabeled,
      },
    })
  }

  // 사용자 지정 필터를 적용 시키는 api
  conditionLabeling(labelingNodeId: string) {
    return this.agent.post(`${ROOT}/${labelingNodeId}/condition-labeling`)
  }

  // 개별 row 라벨링 (tabular)
  updateRowLabels(labelingNodeId: string, payload: { labels: Array<{ label: string, timeNorm: string }> }) {
    return this.agent.post(`${ROOT}/${labelingNodeId}/row-labeling`, payload)
  }

  getTabularChartItems(labelingNodeId: string, startValue: string, endValue: string) {
    return this.agent.get<any>(`${ROOT}/${labelingNodeId}/tabular-chart-items`, {
      params: {
        startValue,
        endValue,
      },
    })
  }

  // regression 타입의 종속, 독립변수 토글 api
  toggleColumSelection(payload: ToggleColumSelectionPayload) {
    const { labelingNodeId, variableRole, column } = payload
    return this.agent.post<LabelingNodeInfo>(`${ROOT}/${labelingNodeId}/toggle-column-selection`, { variableRole, column })
  }
}
